import {useLiveQuery} from 'dexie-react-hooks';
import {offlineDb} from 'src/offline/db';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useAppSelector} from 'src/redux/hooks';

export interface UseOfflineStatusResult {
  isOffline: boolean;
  isSimulated: boolean;
  hasMatchingSession: boolean;
  message: string;
}

/**
 * Shared offline status: connectivity plus whether the active Dexie session
 * belongs to the current instance/user, with the user-facing banner copy.
 */
export const useOfflineStatus = (): UseOfflineStatusResult => {
  const {isOffline, isSimulated} = useConnectivity();
  const instanceId = useAppSelector(state => state.session.instance?.id);
  const userId = useAppSelector(state => state.session.user?.id);

  const activeSession = useLiveQuery(
    () => offlineDb.sessions.where('status').equals('active').first(),
    []
  );

  const hasMatchingSession =
    !!activeSession &&
    activeSession.instanceId === instanceId &&
    activeSession.userId === userId;

  const message =
    (hasMatchingSession
      ? 'You are offline — supported entries will be saved on this device until you reconnect'
      : 'You are offline — changes may not be saved until your connection returns') +
    (isSimulated ? ' (simulated)' : '');

  return {isOffline, isSimulated, hasMatchingSession, message};
};
